import { all, AllEffect, call, delay, fork, ForkEffect, getContext, put, select, takeEvery } from 'redux-saga/effects';
import { retryAPICallChannel } from '..';
import * as api from '../../api/api';
import { IAnyObj, IDocuments } from '../../types';
import { API_CALLS } from '../../utilities/constants';
import isTesting from '../../utilities/isTesting';
import * as AuthActions from '../auth/actions';
import { logout } from '../auth/actions';
import * as DataActions from '../data/actions';
import { getStudent } from '../selectors';
import * as actions from './actions';
import * as DocActions from './documents/actions';
import * as FinDocActions from './financeDocuments/actions';
import { StudentActionTypes, StudentState } from './types';

import { toast } from 'react-toastify';
import { AxiosError } from 'shared-components';
import { ImmigrationStatusShareCodeDoc } from 'types';
import { IVisa } from '../../types';
import { Pages as ConsentPages } from './consents/types';
import { DocActionTypes } from './documents/types';
import { DocActionTypes as FinDocActionTypes } from './financeDocuments/types';
import { getCountryOfVisaApplication } from './helpers';
import { Pages as HistoryPages } from './history/types';
import { Pages as PersonalInfoPages } from './personalInfo/types';
import { Pages as InterviewPages } from './preCasInterview/types';
import { Pages as VisaPages } from './visaPrepForm/types';
import { getDocDetails } from '../../utilities/documents';

function* handleSavePersonalInfo(action: ReturnType<typeof actions.savePersonalInfo>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');

    const { intakeId, offerId } = student.offer;

    let cas: IVisa | null;
    switch (action.payload.source) {
      case PersonalInfoPages.PAGE1:
        cas = yield call(
          api.updateStudent,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            givenName: student.personalInfo.givenName,
            surname: student.personalInfo.surname,
            middleName: student.personalInfo.middleName,
            hasOtherNames: student.personalInfo.hasOtherNames,
            otherNames: student.personalInfo.otherNames
          },
          'pi-step1'
        );
        break;
      case PersonalInfoPages.PAGE2: {
        const nationalities = student.personalInfo.otherNationalities!.map(row => {
          return {
            nationality: row.nationality,
            isCurrent: row.isCurrent ?? false
          };
        });

        const countryOfVisaApplication = getCountryOfVisaApplication(student.personalInfo);

        cas = yield call(
          api.updateStudent,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            gender: student.personalInfo.gender,
            dob: student.personalInfo.dob,
            birthCountry: student.personalInfo.birthCountry,
            nationality: student.personalInfo.nationality,
            appliedForEUSS: student.personalInfo.appliedForEUSS,
            grantedEUSSStatus: student.personalInfo.grantedEUSSStatus,
            applyingFromHomeCountry: student.personalInfo.applyingFromHomeCountry,
            countryOfVisaApplication,
            livedInUKLast12Months: student.personalInfo.livedInUKLast12Months,
            otherCountryReason: student.personalInfo.otherCountryReason,
            hasOtherNationality: student.personalInfo.hasOtherNationality,
            otherNationalities: nationalities
          },
          'pi-step2'
        );
        break;
      }
      case PersonalInfoPages.PAGE3:
        cas = yield call(
          api.updateStudent,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            phoneNumber: student.personalInfo.phoneNumber,
            homeAddress: student.personalInfo.homeAddress,
            city: student.personalInfo.city,
            postCode: student.personalInfo.postCode,
            country: student.personalInfo.country
          },
          'pi-step3'
        );
        break;
      case PersonalInfoPages.PAGE4:
        cas = yield call(
          api.updateStudent,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            hasCriminalConvictions: student.personalInfo.hasCriminalConvictions,
            criminalConvictionReason: student.personalInfo.criminalConvictionReason
          },
          'pi-step4'
        );
        break;

      case PersonalInfoPages.PAGE5: {
        const update: api.PIP5Update = {
          studentNo: student.personalInfo.studentNo,
          courses: student.offer.courses!,
          casReceived: student.offer.casReceived
        };
        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, update, 'pi-step5');
        break;
      }
      case PersonalInfoPages.PAGE5_1: {
        const update: api.PIP5_1Update = {
          courses: student.offer.courses!
        };
        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, update, 'pi-step5_1');
        break;
      }
      case PersonalInfoPages.PAGE6:
        cas = yield call(
          api.updateStudent,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            hasEnglishTest: student.personalInfo.hasEnglishTest,
            englishTestType: student.personalInfo.englishTestType,
            englishTestTypeOther: student.personalInfo.englishTestTypeOther,
            englishTestNotReqdReason: student.personalInfo.englishTestNotReqdReason,
            englishTestNotReqdReasonOther: student.personalInfo.englishTestNotReqdReasonOther,
            qualification: student.personalInfo.qualification,
            qualificationOther: student.personalInfo.qualificationOther
          },
          'pi-step6'
        );
        break;

      case PersonalInfoPages.PAGE7:
        cas = yield call(
          api.updateStudent,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            passportStatus: student.personalInfo.passportStatus,
            passportNumber: student.personalInfo.passportNumber,
            passportExpectedDate: student.personalInfo.passportExpectedDate,
            passportExpiryDate: student.personalInfo.passportExpiryDate
          },
          'pi-step7'
        );
        break;

      case PersonalInfoPages.PAGE8: {
        const update: api.PIP8Update = {
          withAgent: student.personalInfo.withAgent || ''
        };
        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, update, 'pi-step8');
        break;
      }
      case PersonalInfoPages.PAGE9: {
        const update: api.PIP9Update = {
          visaSupport: student.offer.visaSupport!
        };

        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, update, 'pi-step9');
        break;
      }
      case PersonalInfoPages.PAGE10: {
        const update: api.PIP10Update = {
          useWhatsapp: student.personalInfo.useWhatsapp!,
          whatsAppPhoneNumber: student.personalInfo.whatsAppPhoneNumber || ''
        };
        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, update, 'pi-step10');
        break;
      }
      default: {
        cas = null;
      }
    }
    if (cas) {
      yield put(actions.savePersonalInfoSuccess(cas));
    }
  } catch (err) {
    console.log(err);
    if (err instanceof Error) {
      yield put(actions.savePersonalInfoFailure(err.message));
    } else {
      yield put(actions.savePersonalInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveHistory(action: ReturnType<typeof actions.saveHistory>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const student: StudentState = yield select(getStudent);
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;

    let cas: IVisa | null;
    switch (action.payload.source) {
      case HistoryPages.PAGE1:
        cas = yield call(
          api.updateStudent,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            lastCourse: student.history.lastCourse,
            lastCourseOther: student.history.lastCourseOther,
            lastCourseYear: student.history.lastCourseYear,
            hasOverseasTravel: student.history.hasOverseasTravel,
            overseasTravelTimes: student.history.overseasTravelTimes,
            hasUkTravel: student.history.hasUkTravel,
            ukTravelTimes: student.history.ukTravelTimes,
            hasPreviousUkStudy: student.history.hasPreviousUkStudy,
            hasPreviousUkStudyVisa: student.history.hasPreviousUkStudyVisa,
            previousUkStudyVisas: student.history.previousUkStudyVisas,
            lastCourseLevel: student.history.lastCourseLevel,
            lowerOrSameLevelReason: student.history.lowerOrSameLevelReason
          },
          'h-step1'
        );
        break;
      case HistoryPages.PAGE2: {
        const update: api.H2Update = {
          hasOtherUkVisas: student.history.hasOtherUkVisas,
          otherUkVisas: student.history.otherUkVisas,
          hasLeaveToRemain: student.history.hasLeaveToRemain,
          hasBorderRefusal: student.history.hasBorderRefusal,
          borderRefusalReason: student.history.borderRefusalReason,
          hasUkVisaRefusals: student.history.hasUkVisaRefusals,
          ukVisaRefusals: student.history.ukVisaRefusals,
          hasOtherVisaRefusals: student.history.hasOtherVisaRefusals,
          otherVisaRefusals: student.history.otherVisaRefusals
        };

        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, update, 'h-step2');
        break;
      }
      case HistoryPages.PAGE3: {
        const update: api.H3Update = {
          hasDependents: student.offer.hasDependents,
          hasPartner: student.offer.hasPartner,
          partnerVisaStatus: student.offer.partnerVisaStatus,
          hasChildren: student.offer.hasChildren,
          childrenNum: student.offer.childrenNum
        };
        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, update, 'h-step3');
        break;
      }
      default: {
        cas = null;
      }
    }

    if (cas) {
      yield put(actions.saveHistorySuccess(cas));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveHistoryFailure(err.message));
    } else {
      yield put(actions.saveHistoryFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveConsents(action: ReturnType<typeof actions.saveConsents>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    let cas: IVisa | null;
    switch (action.payload.source) {
      case ConsentPages.PAGE1:
        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, {}, 'c-step1');
        break;
      case ConsentPages.PAGE2:
        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, {}, 'c-step2');
        break;
      case ConsentPages.PAGE3:
        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, {}, 'c-step3');
        break;
      case ConsentPages.PAGE4:
        cas = yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, {}, 'c-step4');
        break;
      default:
        cas = null;
    }
    if (cas) {
      yield put(actions.saveConsentsSuccess(cas));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveConsentsFailure(err.message));
    } else {
      yield put(actions.saveConsentsFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveDeposit() {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(
      api.updateStudent,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      {
        courseFeeDepositReqd: student.financials.courseFeeDepositReqd ?? '',
        courseFeeDeposit: student.financials.courseFeeDeposit ?? 0,
        courseFeeDepositDate: student.financials.courseFeeDepositDate ?? '',
        courseFeeDepositNotReqdReason: student.financials.courseFeeDepositNotReqdReason ?? '',
        accomDepositReqd:
          (student.financials.accomDepositReqd ?? '') !== ''
            ? student.financials.accomDepositReqd
            : 'No - Not Required',
        accomDeposit: student.financials.accomDeposit ?? 0,
        accomDepositDate: student.financials.accomDepositDate ?? ''
      },
      'deposit'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveHasViewedBypassWelcomeMessage() {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    yield call(
      api.updateStudent,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      {
        hasViewedBypassWelcomeMessage: true
      },
      'casBypass'
    );
    yield put(actions.saveHasViewedBypassWelcomeMessageSuccess());
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveHasViewedBypassWelcomeMessageFailure(err.message));
    } else {
      yield put(actions.saveHasViewedBypassWelcomeMessageFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateOrganisationSponsor(action: ReturnType<typeof actions.createOrganisationSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.createSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      {
        orgType: data.orgType,
        sponsorName: data.sponsorName,
        isMultiCountry: data.isMultiCountry,
        sponsorshipCovers: data.sponsorshipCovers,
        courseFundType: data.courseFundType,
        courseFundPercentage: data.courseFundPercentage,
        courseFundAmount: data.courseFundAmount,
        livingFundType: data.livingFundType,
        livingFundPercentage: data.livingFundPercentage,
        livingFundAmountType: data.livingFundAmountType,
        livingFundAmount: data.livingFundAmount,
        courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
        courseAndLivingFundType: data.courseAndLivingFundType,
        courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
        courseAndLivingFundAmount: data.courseAndLivingFundAmount
      },
      'company'
    );

    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateOrganisationSponsor(action: ReturnType<typeof actions.updateOrganisationSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.updateSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      {
        orgType: data.orgType,
        sponsorName: data.sponsorName,
        isMultiCountry: data.isMultiCountry,
        sponsorshipCovers: data.sponsorshipCovers,
        courseFundType: data.courseFundType,
        courseFundPercentage: data.courseFundPercentage,
        courseFundAmount: data.courseFundAmount,
        livingFundType: data.livingFundType,
        livingFundPercentage: data.livingFundPercentage,
        livingFundAmountType: data.livingFundAmountType,
        livingFundAmount: data.livingFundAmount,
        courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
        courseAndLivingFundType: data.courseAndLivingFundType,
        courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
        courseAndLivingFundAmount: data.courseAndLivingFundAmount
      },
      'company'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveTravelStatus(action: ReturnType<typeof actions.saveTravelStatus>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    yield call(api.saveTravelStatus, idToken, uni, studentNo, intakeId, offerId, action.payload);
    yield put(actions.getStudentRecord(intakeId, offerId));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveTravelStatusFailure(err.message));
    } else {
      yield put(actions.saveTravelStatusFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveVisaStatus(action: ReturnType<typeof actions.saveVisaStatus>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    yield call(api.saveVisaStatus, idToken, uni, studentNo, intakeId, offerId, action.payload);
    yield put(actions.getStudentRecord(intakeId, offerId));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveVisaStatusFailure(err.message));
    } else {
      yield put(actions.saveVisaStatusFailure('An unknown error occured.'));
    }
  }
}
function* handleSaveRecordedInterviewAttempt(action: ReturnType<typeof actions.saveRecordedInterviewAttempt>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: string = yield call(context.getIdToken);
    const accessToken: string = yield call(context.getAccessToken);

    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }

    const [uni, studentNo] = student.core.student_id.split('#');
    const pathwayProvider: string | undefined = student.core.pathway;

    const { data }: { data: IVisa } = yield call(
      api.saveRecordedInterviewAttempt,
      idToken,
      accessToken,
      uni,
      studentNo,
      action.payload,
      pathwayProvider
    );
    yield put(actions.saveRecordedInterviewAttemptSuccess(data));
  } catch (err) {
    const context = yield getContext('sagaContext');
    const error = err as AxiosError;

    if ([401, 409].includes(error?.response?.status)) {
      toast.error(
        `Attention: Multiple active CAS Shield sessions detected. To ensure an accurate interview process, you have been automatically logged out. Please return to the other session you have open to record your interview.`
      );
      yield put(AuthActions.logout());
      yield call(context.onLogout);
      return;
    }

    if (err instanceof Error) {
      yield put(actions.saveRecordedInterviewAttemptFailure(err.message));
    } else {
      yield put(actions.saveRecordedInterviewAttemptFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveAccommodationStatus(action: ReturnType<typeof actions.saveAccommodationStatus>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    yield call(api.saveAccommodationStatus, idToken, uni, studentNo, intakeId, offerId, action.payload);
    yield put(actions.getStudentRecord(intakeId, offerId));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveAccommodationStatusFailure(err.message));
    } else {
      yield put(actions.saveAccommodationStatusFailure('An unknown error occured.'));
    }
  }
}
function* handleRemoveOrganisationSponsor(action: ReturnType<typeof actions.removeOrganisationSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(
      api.removeSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      'company'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveOrganisationRegion() {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(api.removeRegion, idToken, uni, studentNo, intakeId, offerId, 'company');
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateScholarshipSponsor(action: ReturnType<typeof actions.createScholarshipSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.createSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      {
        scholarshipName: data.scholarshipName,
        sponsorshipCovers: data.sponsorshipCovers,
        courseFundType: data.courseFundType,
        courseFundPercentage: data.courseFundPercentage,
        courseFundAmount: data.courseFundAmount,
        livingFundType: data.livingFundType,
        livingFundPercentage: data.livingFundPercentage,
        livingFundAmountType: data.livingFundAmountType,
        livingFundAmount: data.livingFundAmount,
        courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
        courseAndLivingFundType: data.courseAndLivingFundType,
        courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
        courseAndLivingFundAmount: data.courseAndLivingFundAmount
      },
      'scholarship'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleBulkUpdateScholarshipSponsor(action: ReturnType<typeof actions.bulkUpdateScholarshipSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(AuthActions.logout());
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const payloadData = action.payload.data;
    for (const [index, sponsor] of payloadData.entries()) {
      const { key, data } = sponsor;
      const res: IVisa = yield call(
        api.updateSponsor,
        idToken,
        uni,
        studentNo,
        intakeId,
        offerId,
        key,
        {
          scholarshipName: data.scholarshipName,
          sponsorshipCovers: data.sponsorshipCovers,
          courseFundType: data.courseFundType,
          courseFundPercentage: data.courseFundPercentage,
          courseFundAmount: data.courseFundAmount,
          livingFundType: data.livingFundType,
          livingFundPercentage: data.livingFundPercentage,
          livingFundAmountType: data.livingFundAmountType,
          livingFundAmount: data.livingFundAmount,
          courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
          courseAndLivingFundType: data.courseAndLivingFundType,
          courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
          courseAndLivingFundAmount: data.courseAndLivingFundAmount
        },
        'scholarship'
      );
      if (index === payloadData.length - 1) yield put(actions.saveFinanceInfoSuccess(res));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateScholarshipSponsor(action: ReturnType<typeof actions.updateScholarshipSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.updateSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      {
        scholarshipName: data.scholarshipName,
        sponsorshipCovers: data.sponsorshipCovers,
        courseFundType: data.courseFundType,
        courseFundPercentage: data.courseFundPercentage,
        courseFundAmount: data.courseFundAmount,
        livingFundType: data.livingFundType,
        livingFundPercentage: data.livingFundPercentage,
        livingFundAmountType: data.livingFundAmountType,
        livingFundAmount: data.livingFundAmount,
        courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
        courseAndLivingFundType: data.courseAndLivingFundType,
        courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
        courseAndLivingFundAmount: data.courseAndLivingFundAmount
      },
      'scholarship'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveScholarshipSponsor(action: ReturnType<typeof actions.removeScholarshipSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(
      api.removeSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      'scholarship'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveScholarshipRegion() {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(api.removeRegion, idToken, uni, studentNo, intakeId, offerId, 'scholarship');
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateEducationSponsor(action: ReturnType<typeof actions.createEducationSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.createSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      {
        loanType: data.loanType,
        country: data.country,
        bankName: data.bankName,
        loanName: data.loanName,
        sponsorshipAmount: data.sponsorshipAmount,
        personName: data.personName,
        loanDesc: data.loanDesc,
        personFirstName: data.personFirstName,
        personSurname: data.personSurname,
        personDesc: data.personDesc
      },
      'education'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateEducationSponsor(action: ReturnType<typeof actions.updateEducationSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.updateSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      {
        loanType: data.loanType,
        country: data.country,
        bankName: data.bankName,
        loanName: data.loanName,
        sponsorshipAmount: data.sponsorshipAmount,
        personName: data.personName,
        loanDesc: data.loanDesc,
        personFirstName: data.personFirstName,
        personSurname: data.personSurname,
        personDesc: data.personDesc
      },
      'education'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveEducationSponsor(action: ReturnType<typeof actions.removeEducationSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(
      api.removeSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      'education'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveEducationRegion() {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(api.removeRegion, idToken, uni, studentNo, intakeId, offerId, 'education');
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateFamilySponsor(action: ReturnType<typeof actions.createFamilySponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.createSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      {
        familySponsorType: data.sponsorType,
        accountType: data.accountType,
        jointType: data.jointType,
        accountFirstName: data.accountFirstName,
        accountSurname: data.accountSurname,
        otherReason: data.otherReason,
        country: data.country,
        bankName: data.bankName,
        totalAmount: data.totalAmount,
        sponsorshipAmount: data.sponsorshipAmount,
        canWithdrawImmediately: data.canWithdrawImmediately!
      },
      'family'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateFamilySponsor(action: ReturnType<typeof actions.updateFamilySponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.updateSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      {
        familySponsorType: data.sponsorType,
        accountType: data.accountType,
        jointType: data.jointType,
        accountFirstName: data.accountFirstName,
        accountSurname: data.accountSurname,
        otherReason: data.otherReason,
        country: data.country,
        bankName: data.bankName,
        totalAmount: data.totalAmount,
        sponsorshipAmount: data.sponsorshipAmount,
        canWithdrawImmediately: data.canWithdrawImmediately!
      },
      'family'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveFamilySponsor(action: ReturnType<typeof actions.removeFamilySponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(
      api.removeSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      'family'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveFamilyRegion() {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(api.removeRegion, idToken, uni, studentNo, intakeId, offerId, 'family');
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateOtherSponsor(action: ReturnType<typeof actions.createOtherSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.createSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      {
        otherDesc: data.otherDesc,
        otherName: data.otherName,
        sponsorshipAmount: data.sponsorshipAmount
      },
      'other'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateOtherSponsor(action: ReturnType<typeof actions.updateOtherSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const data = action.payload.data;

    const res: IVisa = yield call(
      api.updateSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      {
        otherDesc: data.otherDesc,
        otherName: data.otherName,
        sponsorshipAmount: data.sponsorshipAmount
      },
      'other'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveOtherSponsor(action: ReturnType<typeof actions.removeOtherSponsor>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(
      api.removeSponsor,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      action.payload.key,
      'other'
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveOtherRegion() {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const res: IVisa = yield call(api.removeRegion, idToken, uni, studentNo, intakeId, offerId, 'other');
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleGetOfferRecord(action: ReturnType<typeof actions.getStudentRecord>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }

    const { intakeId, offerId } = action.payload;

    let studentNo;
    let uni;

    if (!isTesting) {
      studentNo = context.studentNo;
      uni = context.uni;
    } else {
      studentNo = '0000003';
      uni = 'TST';
    }

    const resp: { student: IVisa; config: any; uniConfig: any } = yield call(
      api.getStudentRecord,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      (retryNumber: number, time: number) => {
        retryAPICallChannel.put(DataActions.retryAPICall(API_CALLS.GET_STUDENT_RECORD, retryNumber, time));
      },
      () => {
        retryAPICallChannel.put(DataActions.retryAPICallSuccess(API_CALLS.GET_STUDENT_RECORD));
      },
      (e: Error) => {
        retryAPICallChannel.put(DataActions.retryAPICallFailure(API_CALLS.GET_STUDENT_RECORD, e.message));
      }
    );
    yield put(actions.getStudentRecordSuccess(resp.student, resp.config, resp.uniConfig));
  } catch (err) {
    console.log('student error', err);
    if (err instanceof Error) {
      yield put(actions.getStudentRecordFailure(err.message));
    } else {
      yield put(actions.getStudentRecordFailure('An unknown error occured.'));
    }
  }
}
function* handleGetStudentDocs() {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: any = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }

    const resp: { documents: IDocuments } = yield call(
      api.getStudentDocs,
      idToken,
      student.core.university,
      student.core.student_number,
      student.offer.intakeId,
      student.offer.offerId,
      (retryNumber: number, time: number) => {
        retryAPICallChannel.put(DataActions.retryAPICall(API_CALLS.GET_STUDENT_DOCS, retryNumber, time));
      },
      () => {
        retryAPICallChannel.put(DataActions.retryAPICallSuccess(API_CALLS.GET_STUDENT_DOCS));
      },
      (e: Error) => {
        retryAPICallChannel.put(DataActions.retryAPICallFailure(API_CALLS.GET_STUDENT_DOCS, e.message));
      }
    );
    yield put(actions.getStudentDocsSuccess(resp.documents));
  } catch (err) {
    console.log('err 1', err);
    if (err instanceof Error) {
      yield put(actions.getStudentRecordFailure(err.message));
    } else {
      yield put(actions.getStudentRecordFailure('An unknown error occured.'));
    }
  }
}
function* handleGetStudentFinDocs() {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: any = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }

    const resp: { financeDocuments: IDocuments } = yield call(
      api.getStudentFinDocs,
      idToken,
      student.core.university,
      student.core.student_number,
      student.offer.intakeId,
      student.offer.offerId,
      (retryNumber: number, time: number) => {
        retryAPICallChannel.put(DataActions.retryAPICall(API_CALLS.GET_STUDENT_DOCS, retryNumber, time));
      },
      () => {
        retryAPICallChannel.put(DataActions.retryAPICallSuccess(API_CALLS.GET_STUDENT_DOCS));
      },
      (e: Error) => {
        retryAPICallChannel.put(DataActions.retryAPICallFailure(API_CALLS.GET_STUDENT_DOCS, e.message));
      }
    );
    yield put(actions.getStudentFinDocsSuccess(resp.financeDocuments));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.getStudentRecordFailure(err.message));
    } else {
      yield put(actions.getStudentRecordFailure('An unknown error occured.'));
    }
  }
}
function* handleGetStudentFollowUpDocs() {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: any = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }

    const resp: { followUpDocuments: IDocuments } = yield call(
      api.getStudentFollowUpDocs,
      idToken,
      student.summary.uni,
      student.summary.student_id,
      student.offer.intakeId,
      student.offer.offerId,
      (retryNumber: number, time: number) => {
        retryAPICallChannel.put(DataActions.retryAPICall(API_CALLS.GET_STUDENT_FOLLOW_UP_DOCS, retryNumber, time));
      },
      () => {
        retryAPICallChannel.put(DataActions.retryAPICallSuccess(API_CALLS.GET_STUDENT_FOLLOW_UP_DOCS));
      },
      (e: Error) => {
        retryAPICallChannel.put(DataActions.retryAPICallFailure(API_CALLS.GET_STUDENT_FOLLOW_UP_DOCS, e.message));
      }
    );
    yield put(actions.getStudentFollowUpDocsSuccess(resp.followUpDocuments));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.getStudentRecordFailure(err.message));
    } else {
      yield put(actions.getStudentRecordFailure('An unknown error occured.'));
    }
  }
}
function* handleGetStudentMessages(): any {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: StudentState = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired');
    }
    const res: any = yield call(
      api.getStudentMessages,
      idToken,
      student.core.university,
      student.core.student_number,
      student.offer.intakeId,
      student.offer.offerId
    );
    if (res) {
      yield put(actions.getStudentMessagesSuccess(res.messages));
    }
  } catch (err: any) {
    yield put(actions.getStudentRecordFailure(err.message));
  }
}
function* handleSavePreCasInterview(action: ReturnType<typeof actions.savePreCasInterview>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    let cas: IVisa | null;
    switch (action.payload) {
      case InterviewPages.PAGE1: {
        const update: api.I1Update = {
          whyUk: student.preCasInterview.whyUk || '',
          whyUkOther: student.preCasInterview.whyUkOther || '',
          selectedCourseReasons: student.preCasInterview.selectedCourseReasons || [],
          selectedCourseReasonsOther: student.preCasInterview.selectedCourseReasonsOther || '',
          selectedUniReasons: student.preCasInterview.selectedUniReasons || [],
          selectedUniReasonsOther: student.preCasInterview.selectedUniReasonsOther || '',
          uniChoiceReason1: student.preCasInterview.uniChoiceReason1 || '',
          uniChoiceReason2: student.preCasInterview.uniChoiceReason2 || '',
          uniChoiceReason3: student.preCasInterview.uniChoiceReason3 || ''
        };
        cas = yield call(api.updateInterview, idToken, uni, studentNo, intakeId, offerId, update, 'i-step1');
        break;
      }
      case InterviewPages.PAGE2: {
        const update: api.I2Update = {
          campus: student.preCasInterview.campus || '',
          considerOtherUni: student.preCasInterview.considerOtherUni,
          otherUni1: student.preCasInterview.otherUni1 || '',
          otherUni2: student.preCasInterview.otherUni2 || '',
          selectedResearchMethods: student.preCasInterview.selectedResearchMethods!,
          selectedResearchMethodsOther: student.preCasInterview.selectedResearchMethodsOther || '',
          whyNotOtherUni: student.preCasInterview.whyNotOtherUni || '',
          considerOtherCourses: student.preCasInterview.considerOtherCourses,
          considerOtherCourse: student.preCasInterview.considerOtherCourse || '',
          whyNotConsiderOtherCourse: student.preCasInterview.whyNotConsiderOtherCourse || '',
          courseAlignFutureCareer: student.preCasInterview.courseAlignFutureCareer,
          courseAlignFutureCareerOther: student.preCasInterview.courseAlignFutureCareerOther || ''
        };

        cas = yield call(api.updateInterview, idToken, uni, studentNo, intakeId, offerId, update, 'i-step2');
        break;
      }

      case InterviewPages.PAGE3: {
        const update: api.I3Update = {
          workWhileStudy: student.preCasInterview.workWhileStudy,
          maxWorkHoursPerWeek: Number(student.preCasInterview.maxWorkHoursPerWeek),
          whyWorkWhileStudy: student.preCasInterview.whyWorkWhileStudy!,
          whyWorkWhileStudyOther: student.preCasInterview.whyWorkWhileStudyOther || '',
          affordCourseAndLivingWithoutWorking: student.preCasInterview.affordCourseAndLivingWithoutWorking,
          affordCourseAndLivingWithoutWorkingNo: student.preCasInterview.affordCourseAndLivingWithoutWorkingNo || '',
          completeDegreeWorkCountry: student.preCasInterview.completeDegreeWorkCountry || '',
          expectedSalary: Number(student.preCasInterview.expectedSalary),
          furtherStudyInUk: student.preCasInterview.furtherStudyInUk,
          furtherStudyCareerPlans: student.preCasInterview.furtherStudyCareerPlans || ''
        };
        cas = yield call(api.updateInterview, idToken, uni, studentNo, intakeId, offerId, update, 'i-step3');
        break;
      }
      case InterviewPages.PAGE4: {
        const update: api.I4Update = {
          liveWhileStudying: student.preCasInterview.liveWhileStudying || '',
          liveWhileStudyingOther: student.preCasInterview.liveWhileStudyingOther || '',
          expectedWeeklyRent: Number(student.preCasInterview.expectedWeeklyRent) || 0,
          commutePlan: student.preCasInterview.commutePlan || '',
          expectedLivingExpenses: Number(student.preCasInterview.expectedLivingExpenses) || 0,
          awareOfUKVIInterview: student.preCasInterview.awareOfUKVIInterview
        };

        cas = yield call(api.updateInterview, idToken, uni, studentNo, intakeId, offerId, update, 'i-step4');
        break;
      }
      default: {
        cas = null;
      }
    }
    if (cas) {
      yield put(actions.savePreCasInterviewSuccess(cas));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.savePreCasInterviewFailure(err.message));
    } else {
      yield put(actions.savePreCasInterviewFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveVisaPrepForm(action: ReturnType<typeof actions.saveVisaPrepForm>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    let cas: IVisa | null;
    switch (action.payload) {
      case VisaPages.PAGE1: {
        const passports: Array<api.Passport> = student.visa.additionalPassports!.map(passport => {
          return {
            docNumber: passport.docNumber || '',
            placeOfIssue: passport.placeOfIssue || '',
            issuingAuthority: passport.issuingAuthority || '',
            issueDate: passport.issueDate || '',
            expiryDate: passport.expiryDate || ''
          };
        });

        const update: api.V1Update = {
          hasAdditionalPassports: student.visa.hasAdditionalPassports!,
          additionalPassports: passports
        };

        cas = yield call(api.updateVisaPrep, idToken, uni, studentNo, intakeId, offerId, update, 'v-step1');
        break;
      }
      case VisaPages.PAGE1_2: {
        const update: api.V12Update = {
          residentialAddressStartDate: student.visa.residentialAddressStartDate || '',
          homeOwnershipStatus: student.visa.homeOwnershipStatus || '',
          homeOwnershipStatusOther: student.visa.homeOwnershipStatusOther || '',
          hasDifferentContactDetails: student.visa.hasDifferentContactDetails,
          diffContactAddress: student.visa.diffContactAddress || '',
          diffContactCity: student.visa.diffContactCity || '',
          diffContactPostcode: student.visa.diffContactPostcode || '',
          diffContactCountry: student.visa.diffContactCountry || ''
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step1-2'
        );
        break;
      }
      case VisaPages.PAGE2_1:
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            nonHomeCountryApplicationDesc: student.visa.nonHomeCountryApplicationDesc || ''
          },
          'v-step2-1'
        );
        break;

      case VisaPages.PAGE2_2: {
        const otherUkVisas: Array<api.OtherUkVisas> = student.visa.otherUkVisas!.map(visa => {
          return {
            visaType: visa.visaType || '',
            issueDate: visa.issueDate || '',
            expiryDate: visa.expiryDate || '',
            country: visa.country || '',
            issuingAuthority: visa.issuingAuthority || ''
          };
        });

        const update: api.V22Update = {
          hasOtherUkVisas: student.visa.hasOtherUkVisas!,
          otherUkVisas: otherUkVisas
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step2-2'
        );
        break;
      }
      case VisaPages.PAGE2_3: {
        const tripsToUk: Array<api.TripsToUK> = student.visa.tripsToUk!.map(trips => {
          return {
            arrivalDate: trips.arrivalDate || '',
            departureDate: trips.departureDate || '',
            purpose: trips.purpose || ''
          };
        });

        const update: api.V23Update = {
          tripsToUk: tripsToUk
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step2-3'
        );
        break;
      }
      case VisaPages.PAGE2_4: {
        const exclusionOrders: Array<api.ExclusionOrders> = student.visa.exclusionOrders!.map(orders => {
          return {
            exclusionDate: orders.exclusionDate || '',
            reason: orders.reason,
            reference: orders.reference
          };
        });

        const update: api.V24Update = {
          hasBeenToUk: student.visa.hasBeenToUk!,
          hasNINumber: student.visa.hasNINumber,
          niNumber: student.visa.niNumber || '',
          hasReceivedMedicalTreatment: student.visa.hasReceivedMedicalTreatment,
          hasVoluntarilyDeparted: student.visa.hasVoluntarilyDeparted,
          hasExclusionOrder: student.visa.hasExclusionOrder,
          exclusionOrders: exclusionOrders
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step2-4'
        );
        break;
      }
      case VisaPages.PAGE2_5: {
        const leaveToRemains: Array<api.LeaveToRemains> = student.visa.leaveToRemains!.map(leave => {
          return {
            applicationDate: leave.applicationDate || '',
            applicationType: leave.applicationType || '',
            reference: leave.reference || ''
          };
        });

        const update: api.V25Update = {
          leaveToRemains: leaveToRemains
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step2-5'
        );
        break;
      }
      case VisaPages.PAGE2_6: {
        const borderRefusals: Array<api.BorderRefusals> = student.visa.borderRefusals!.map(refusal => {
          return {
            reference: refusal.reference,
            refusalDate: refusal.refusalDate || '',
            airport: refusal.airport || '',
            reason: refusal.reason || ''
          };
        });

        const update: api.V26Update = {
          borderRefusals: borderRefusals
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step2-6'
        );
        break;
      }
      case VisaPages.PAGE2_7: {
        const deportations: Array<api.Deportations> = student.visa.deportations!.map(deportation => {
          return {
            country: deportation.country || '',
            removalDate: deportation.removalDate || '',
            airport: deportation.airport || '',
            reason: deportation.reason || ''
          };
        });

        const update: api.V27Update = {
          hasBeenDeported: student.visa.hasBeenDeported!,
          deportations: deportations
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step2-7'
        );
        break;
      }
      case VisaPages.PAGE2_8: {
        const travelItems: Array<api.TravelItems> = student.visa.travelItems!.map(travelItem => {
          return {
            country: travelItem.country || '',
            reason: travelItem.reason || '',
            reasonOther: travelItem.reasonOther || '',
            entryDate: travelItem.entryDate || '',
            exitDate: travelItem.exitDate || ''
          };
        });

        const update: api.V28Update = {
          hasTravelled: student.visa.hasTravelled!,
          travelItems: travelItems
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step2-8'
        );
        break;
      }
      case VisaPages.PAGE3_1: {
        const update: api.V31Update = {
          maritalStatus: student.visa.maritalStatus || '',
          spouseNationality: student.visa.spouseNationality || '',
          spouseSameNationality: student.visa.spouseSameNationality,
          spouseFamilyName: student.visa.spouseFamilyName || '',
          spouseGivenName: student.visa.spouseGivenName || '',
          spouseDob: student.visa.spouseDob || '',
          spouseLivingWith: student.visa.spouseLivingWith,
          spouseTravellingWith: student.visa.spouseTravellingWith || '',
          spouseUkVisa: student.visa.spouseUkVisa,
          spouseHasPassport: student.visa.spouseHasPassport,
          spousePassportNumber: student.visa.spousePassportNumber || ''
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step3-1'
        );
        break;
      }
      case VisaPages.PAGE3_2: {
        const update: api.V32Update = {
          hasFathersPersonalDetails: student.visa.hasFathersPersonalDetails!,
          fatherNationality: student.visa.fatherNationality || '',
          fatherSameNationality: student.visa.fatherSameNationality,
          fatherFamilyName: student.visa.fatherFamilyName || '',
          fatherGivenName: student.visa.fatherGivenName || '',
          fatherDob: student.visa.fatherDob || '',
          fatherTravellingWith: student.visa.fatherTravellingWith || '',
          fatherPlaceOfBirth: student.visa.fatherPlaceOfBirth || '',
          fatherUkVisa: student.visa.fatherUkVisa,
          fatherHasPassport: student.visa.fatherHasPassport,
          fatherPassportNumber: student.visa.fatherPassportNumber || ''
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step3-2'
        );
        break;
      }
      case VisaPages.PAGE3_3: {
        const update: api.V33Update = {
          hasMothersPersonalDetails: student.visa.hasMothersPersonalDetails!,
          motherNationality: student.visa.motherNationality || '',
          motherSameNationality: student.visa.motherSameNationality,
          motherFamilyName: student.visa.motherFamilyName || '',
          motherGivenName: student.visa.motherGivenName || '',
          motherDob: student.visa.motherDob || '',
          motherTravellingWith: student.visa.motherTravellingWith || '',
          motherPlaceOfBirth: student.visa.motherPlaceOfBirth || '',
          motherUkVisa: student.visa.motherUkVisa,
          motherHasPassport: student.visa.motherHasPassport,
          motherPassportNumber: student.visa.motherPassportNumber || ''
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step3-3'
        );
        break;
      }
      case VisaPages.PAGE3_4: {
        const update: api.V34Update = {
          hasGuardiansPersonalDetails: student.visa.hasGuardiansPersonalDetails!,
          guardianNationality: student.visa.guardianNationality || '',
          guardianSameNationality: student.visa.guardianSameNationality,
          guardianFamilyName: student.visa.guardianFamilyName || '',
          guardianGivenName: student.visa.guardianGivenName || '',
          guardianDob: student.visa.guardianDob || '',
          guardianTravellingWith: student.visa.guardianTravellingWith || '',
          guardianPlaceOfBirth: student.visa.guardianPlaceOfBirth || '',
          guardianUkVisa: student.visa.guardianUkVisa,
          guardianHasPassport: student.visa.guardianHasPassport,
          guardianPassportNumber: student.visa.guardianPassportNumber || ''
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step3-4'
        );
        break;
      }
      case VisaPages.PAGE3_5: {
        const children: Array<api.Children> = student.visa.children!.map(child => {
          return {
            nationality: child.nationality || '',
            gender: child.gender || '',
            sameNationality: child.sameNationality,
            familyName: child.familyName || '',
            givenName: child.givenName || '',
            dob: child.dob || '',
            placeOfBirth: child.placeOfBirth || '',
            travellingWith: child.travellingWith || '',
            ukVisa: child.ukVisa,
            hasPassport: child.hasPassport,
            passportNumber: child.passportNumber || ''
          };
        });

        const update = {
          hasDependantChildren: student.visa.hasDependantChildren!,
          children: children,
          childrenLivingWithYou: student.visa.childrenLivingWithYou,
          contactAddress: student.visa.contactAddress,
          contactCity: student.visa.contactCity,
          contactCountry: student.visa.contactCountry,
          contactPostcode: student.visa.contactPostcode,
          contactNumber: student.visa.contactNumber
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step3-5'
        );
        break;
      }
      case VisaPages.PAGE4: {
        const otherTravellers: Array<api.OtherTravellers> = student.visa.otherTravellers!.map(traveller => {
          return {
            relationship: traveller.relationship || '',
            relationshipOther: traveller.relationshipOther || '',
            adultOrChild: traveller.adultOrChild || '',
            nationality: traveller.nationality || '',
            sameNationality: traveller.sameNationality,
            familyName: traveller.familyName || '',
            givenName: traveller.givenName || '',
            dob: traveller.dob,
            ukVisa: traveller.ukVisa,
            hasPassport: traveller.hasPassport,
            passportNumber: traveller.passportNumber || ''
          };
        });

        const update: api.V4Update = {
          travellingWithOthers: student.visa.travellingWithOthers!,
          otherTravellers: otherTravellers
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step4'
        );
        break;
      }

      case VisaPages.PAGE5: {
        const family: Array<api.Family> = student.visa.familyMembers!.map(family => {
          return {
            nationality: family.nationality || '',
            familyName: family.familyName || '',
            givenName: family.givenName || '',
            dob: family.dob || ''
          };
        });

        const update: api.V5Update = {
          hasArrivalDate: student.visa.hasArrivalDate!,
          arrivalDate: student.visa.arrivalDate || '',
          familyContactAddress: student.visa.familyContactAddress || '',
          familyContactCity: student.visa.familyContactCity || '',
          familyContactCountry: student.visa.familyContactCountry || '',
          familyContactPostcode: student.visa.familyContactPostcode || '',
          familyPhoneNumber: student.visa.familyPhoneNumber || '',
          familyEmail: student.visa.familyEmail || '',
          family: family,
          hasOtherContactAddress: student.visa.hasOtherContactAddress!,
          lengthOfStayDays: Number(student.visa.lengthOfStayDays) || 0,
          lengthOfStayMonths: Number(student.visa.lengthOfStayMonths) || 0,
          lengthOfStayYears: Number(student.visa.lengthOfStayYears) || 0,
          otherContactAddress: student.visa.otherContactAddress || '',
          otherContactCity: student.visa.otherContactCity || '',
          otherContactCountry: student.visa.otherContactCountry || '',
          otherContactPostcode: student.visa.otherContactPostcode || '',
          otherPhoneNumber: student.visa.otherPhoneNumber || '',
          stayingWithFamily: student.visa.stayingWithFamily!,
          ukFamily: student.visa.ukFamily!,
          ukPresence: student.visa.ukPresence!
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step5'
        );
        break;
      }
      case VisaPages.PAGE6: {
        const update: api.V6Update = {
          hasCriminalCharges: student.visa.hasCriminalCharges!,
          hasTerrorismInvolvement: student.visa.hasTerrorismInvolvement!,
          hasTerrorismConnection: student.visa.hasTerrorismConnection!,
          hasTerrorismJustified: student.visa.hasTerrorismJustified!,
          hasWarCrimes: student.visa.hasWarCrimes!,
          hasBadActivities: student.visa.hasBadActivities!,
          language: student.visa.language || ''
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step6'
        );
        break;
      }
      case VisaPages.PAGE7_1: {
        const update: api.V71Update = {
          hasReceivedCas: student.visa.hasReceivedCas!,
          studiesConfirmationNumber: student.visa.studiesConfirmationNumber || '',
          courseEndDate: student.visa.courseEndDate || '',
          alreadyStartedStudying: student.visa.alreadyStartedStudying!,
          studyAddress: student.visa.studyAddress || '',
          assessedBySponsor: student.visa.assessedBySponsor!
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          update,

          'v-step7-1'
        );
        break;
      }
      case VisaPages.PAGE7_2:
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            qualificationAndCountry: student.visa.qualificationAndCountry || '',
            qualificationAsProofOfEnglish: student.visa.qualificationAsProofOfEnglish!
          },
          'v-step7-2'
        );
        break;
      case VisaPages.PAGE7_3:
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            appliedThroughUCAS: student.visa.appliedThroughUCAS!,
            ucasPin: student.visa.ucasPin || ''
          },
          'v-step7-3'
        );
        break;
      case VisaPages.PAGE8:
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          uni,
          studentNo,
          intakeId,
          offerId,
          {
            otherFeedback: student.visa.otherFeedback || '',
            difficultiesFeedback: student.visa.difficultiesFeedback || ''
          },
          'v-step8'
        );
        break;
      default:
        cas = null;
    }
    if (cas) {
      yield put(actions.saveVisaPrepFormSuccess(cas));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveVisaPrepFormFailure(err.message));
    } else {
      yield put(actions.saveVisaPrepFormFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveMessage(action: any) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    yield call(api.saveMessage, idToken, uni, studentNo, intakeId, offerId, action.payload);
    yield put(actions.saveMessageSuccess(action.payload));
  } catch (err) {
    yield put(actions.saveMessageFailure(action.payload));
  }
}

function* handleUnsubscribe(action: any) {
  try {
    yield call(api.unsubscribe, action.payload.email, action.payload.studentID);
    yield put(actions.unsubscribeSuccess());
  } catch (err) {
    yield put(actions.unsubscribeSuccess());
  }
}

function* handleWebsocketMessage(action: any): any {
  // console.log('handleWebsocketMessage...', action);
  delay(1000);
  const { action: serverAction, data } = JSON.parse(action.payload.data);
  const parsedData = JSON.parse(data);
  const student: StudentState = yield select(getStudent);
  const { intakeId, offerId } = student?.offer ?? { intakeId: undefined, offerId: undefined };
  const { intakeId: msgIntakeId, offerId: msgOfferId } = parsedData ?? {
    intakeId: undefined,
    offerId: undefined
  };

  if (intakeId !== msgIntakeId || offerId !== msgOfferId) {
    console.log('mismatched intake / offer', intakeId, offerId, msgIntakeId, msgOfferId);
    return;
  }

  switch (serverAction) {
    // case 'newMessage': {
    //   yield put(actions.newMessage(message.payload));
    //   break;
    // }
    case 'updateCalendlyInterviews': {
      yield put(actions.updateCalendlyInterviewWS(parsedData.offer));
      break;
    }
    case 'updateSummary': {
      yield put(actions.updateSummary(parsedData));
      break;
    }
    default:
      break;
  }
}

function* handleUpdateATASApplied(action: ReturnType<typeof actions.saveDocuments>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { docKey } = action.payload;

    const docs: IDocuments = yield call(api.updateATASApplied, idToken, uni, studentNo, intakeId, offerId, docKey!);
    yield put(DocActions.updateATASSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(DocActions.updateATASFailure(err.message));
    } else {
      yield put(DocActions.updateATASFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveFinanceMetaDataForNoDoc(action: ReturnType<typeof FinDocActions.saveMetaDataForNoDoc>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { sponsorType, sponsorKey, docType, docKey, metadata } = action.payload;

    const body = {
      ...metadata
    };
    const docs: unknown = yield call(
      api.updateFinanceMetaDataForNoDoc,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey!,
      body
    );
    yield put(FinDocActions.saveMetaDataForNoDocSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(FinDocActions.saveMetaDataForNoDocFailure(err.message));
    } else {
      yield put(FinDocActions.saveMetaDataForNoDocFailure('An unknown error occured.'));
    }
  }
}

function* handleAddRequiredDoc(action: ReturnType<typeof DocActions.addRequiredDocument>) {
  try {
    const student: StudentState = yield select(getStudent);
    const { intakeId, offerId } = student.offer;
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey, newDocType, metadata } = action.payload;

    const body = {
      newDocType,
      ...metadata
    };
    const docs: IDocuments = yield call(
      api.addRequiredDoc,
      idToken,
      student.core.university,
      student.core.student_number,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey!,
      body
    );
    yield put(DocActions.addRequiredDocumentSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(DocActions.addRequiredDocumentFailure(err.message));
    } else {
      yield put(DocActions.addRequiredDocumentFailure('An unknown error occured.'));
    }
  }
}

function* handleAddRequiredFinanceDoc(action: ReturnType<typeof FinDocActions.addRequiredDocument>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { sponsorType, sponsorKey, docType, docKey, newDocType, metadata } = action.payload;

    const body = {
      newDocType,
      ...metadata
    };
    const docs: IFinanceDocuments = yield call(
      api.addRequiredFinanceDoc,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey!,
      body
    );
    yield put(FinDocActions.addRequiredDocumentSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(FinDocActions.addRequiredDocumentFailure(err.message));
    } else {
      yield put(FinDocActions.addRequiredDocumentFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateVisaRefusalMetaData(action: ReturnType<typeof DocActions.updateVisaRefusalMetaData>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { sponsorType, sponsorKey, docKey, metadata } = action.payload;

    const body = {
      ...metadata
    };
    const docs: IDocuments = yield call(
      api.updateVisaRefusalNoDoc,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docKey!,
      body
    );
    yield put(DocActions.updateVisaRefusalMetaDataSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(DocActions.updateVisaRefusalMetaDataFailure(err.message));
    } else {
      yield put(DocActions.updateVisaRefusalMetaDataFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateTBTestNoDoc(action: ReturnType<typeof DocActions.updateTBTestMetaData>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { docKey, metadata } = action.payload;

    const body = {
      ...metadata
    };

    const docs: IDocuments = yield call(
      api.updateTBTestNoDoc,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      docKey!,
      body
    );
    yield put(DocActions.updateTBSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(DocActions.updateTBFailure(err.message));
    } else {
      yield put(DocActions.updateTBFailure('An unknown error occured.'));
    }
  }
}

function* handleToggleAcknowledge(action: ReturnType<typeof DocActions.toggleAcknowledge>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { sponsorType, sponsorKey, docType, docKey, status, infoOnlyRequirementsOption, metadata } = action.payload;

    const docs: IDocuments = yield call(
      api.toggleDocStatus,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      status,
      infoOnlyRequirementsOption,
      metadata
    );
    yield put(DocActions.toggleAcknowledgeSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(DocActions.toggleAcknowledgeFailure(err.message));
    } else {
      yield put(DocActions.toggleAcknowledgeFailure('An unknown error occured.'));
    }
  }
}

function* handleToggleFinAcknowledge(action: ReturnType<typeof FinDocActions.toggleAcknowledge>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { sponsorType, sponsorKey, docType, docKey, status, infoOnlyRequirementsOption, metadata } = action.payload;

    const docs: IFinanceDocuments = yield call(
      api.toggleFinDocStatus,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      status,
      infoOnlyRequirementsOption,
      metadata
    );
    yield put(FinDocActions.toggleAcknowledgeSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(FinDocActions.toggleAcknowledgeFailure(err.message));
    } else {
      yield put(FinDocActions.toggleAcknowledgeFailure('An unknown error occured.'));
    }
  }
}

function* handleNoBiometricsDoc(action: ReturnType<typeof actions.saveDocuments>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;

    const doc = {
      ...student.documents[sponsorType][sponsorKey!].biometric![docKey!],
      hasDocument: false,
      status: 'SKIPPED'
    };

    const body = {
      ...doc
    };
    const docs: IDocuments = yield call(
      api.saveDocs,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body
    );
    yield put(actions.saveDocumentsSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveDocumentsFailure(err.message));
    } else {
      yield put(actions.saveDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveDocuments(action: ReturnType<typeof actions.saveDocuments>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;

    const doc = getDocDetails({ docs: student.documents, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

    const meta = {} as IAnyObj;
    if (
      ['birthCertificate', 'visaRefusals', 'nonUkVisaRefusals', 'otherVisas', 'customRequest', 'studentVisas'].includes(
        sponsorType
      )
    ) {
      let docRec: any;
      if (sponsorType === 'birthCertificate') {
        docRec = student.documents[sponsorType][docType!][docKey!];
      } else {
        docRec = student.documents[sponsorType][sponsorKey!];
      }
      Object.entries(docRec).forEach(([key, value]) => {
        if (typeof value !== 'object') {
          meta[key] = value;
        }
      });
    }
    // only mark as fixed once a doc has been uploaded
    if (doc.isDocumentFixedPending) {
      delete doc.isDocumentFixedPending;
      if (doc.docCount !== doc.fileNames.length) {
        doc.isDocumentFixed = true;
      } else {
        // return here to prevent an API request as no doc has been uploaded
        yield put(actions.saveFinanceDocumentsFailure('No new document added'));
        return null;
      }
    }
    const body = {
      ...meta,
      ...doc
    };

    const docs: IDocuments = yield call(
      api.saveDocs,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body
    );
    yield put(actions.saveDocumentsSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveDocumentsFailure(err.message));
    } else {
      yield put(actions.saveDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveFollowUpDocuments(action: ReturnType<typeof actions.saveFollowUpDocuments>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;

    const doc = getDocDetails({ docs: student.followUpDocuments, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

    const meta = {} as IAnyObj;
    if (['beforeTravel', 'afterArrival'].includes(sponsorType)) {
      let followUpDoc: any;
      if (sponsorKey === null) {
        followUpDoc = student.followUpDocuments[sponsorType][docType!];
      } else {
        // for custom docs
        followUpDoc = student.followUpDocuments[sponsorType][docType!][sponsorKey!];
      }
      Object.entries(followUpDoc).forEach(([key, value]) => {
        if (typeof value !== 'object') {
          meta[key] = value;
        }
      });
    }

    // only mark as fixed once a doc has been uploaded
    if (doc.isDocumentFixedPending) {
      delete doc.isDocumentFixedPending;
      if (doc.docCount !== doc.fileNames.length) {
        doc.isDocumentFixed = true;
      } else {
        // return here to prevent an API request as no doc has been uploaded
        yield put(actions.saveFollowUpDocumentsFailure('No new document added'));
        return null;
      }
    }
    const body = {
      ...meta,
      ...doc
    };
    const docs: IFinanceDocuments = yield call(
      api.saveFollowUpDocs,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body
    );
    yield put(actions.getStudentRecord(intakeId, offerId));
    yield put(actions.saveFollowUpDocumentsSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFollowUpDocumentsFailure(err.message));
    } else {
      yield put(actions.saveFollowUpDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveFollowUpDocumentsBRPWillNotReceiving(
  action: ReturnType<typeof actions.saveFollowUpDocumentsBRPWillNotReceiving>
) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { docKey, reason } = action.payload;

    const body = {
      notBeReceivingReason: reason,
      fileNames: [],
      status: 'UPLOADED_OK'
    };

    const docs: IFinanceDocuments = yield call(
      api.saveFollowUpDocs,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      'afterArrival',
      null,
      'biometricResidencePermit',
      docKey,
      body
    );
    yield put(actions.getStudentRecord(intakeId, offerId));
    yield put(actions.saveFollowUpDocumentsSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFollowUpDocumentsFailure(err.message));
    } else {
      yield put(actions.saveFollowUpDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveFollowUpDocumentShareCode(action: ReturnType<typeof actions.saveFollowUpDocumentsShareCode>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { docKey, data } = action.payload;

    const doc: Partial<ImmigrationStatusShareCodeDoc> =
      student?.followUpDocuments?.beforeTravel?.immigrationStatusShareCode?.[docKey] ?? {};
    const body = {
      fileNames: [],
      ...doc,
      ...data,
      status: 'UPLOADED_OK'
    };

    const docs: IFinanceDocuments = yield call(
      api.saveFollowUpDocs,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      'beforeTravel',
      null,
      'immigrationStatusShareCode',
      docKey,
      body
    );
    yield put(actions.getStudentRecord(intakeId, offerId));
    yield put(actions.saveFollowUpDocumentsSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFollowUpDocumentsFailure(err.message));
    } else {
      yield put(actions.saveFollowUpDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveFinanceDocuments(action: ReturnType<typeof actions.saveFinanceDocuments>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;

    const doc = getDocDetails({ docs: student.financeDocuments, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

    const meta = {} as IAnyObj;
    if (
      [
        'birthCertificate',
        'governmentSponsorship',
        'companySponsorship',
        'educationSponsorship',
        'scholarshipSponsorship',
        'customRequest',
        'familySponsorship'
      ].includes(sponsorType)
    ) {
      let financeDocRec: any;
      if (sponsorType === 'birthCertificate') {
        financeDocRec = student.financeDocuments[sponsorType][docType!][docKey!];
      } else {
        financeDocRec = student.financeDocuments[sponsorType][sponsorKey!];
      }
      Object.entries(financeDocRec).forEach(([key, value]) => {
        if (typeof value !== 'object') {
          meta[key] = value;
        }
      });
    }
    // only mark as fixed once a doc has been uploaded
    if (doc.isDocumentFixedPending) {
      delete doc.isDocumentFixedPending;
      if (doc.docCount !== doc.fileNames.length) {
        doc.isDocumentFixed = true;
      } else {
        // return here to prevent an API request as no doc has been uploaded
        yield put(actions.saveFinanceDocumentsFailure('No new document added'));
        return null;
      }
    }
    const body = {
      ...meta,
      ...doc
    };
    const docs: IFinanceDocuments = yield call(
      api.saveFinanceDocs,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body
    );
    yield put(actions.saveFinanceDocumentsSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceDocumentsFailure(err.message));
    } else {
      yield put(actions.saveFinanceDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleMarkMessagesRead(action: ReturnType<typeof actions.markMessagesRead>) {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const messageIds = (action.payload || []).filter(message => !!message);

    yield call(api.markMessagesRead, idToken, uni, studentNo, intakeId, offerId, messageIds);
    yield put(actions.markMessagesReadSuccess(messageIds));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.markMessagesReadFailure(err.message));
    } else {
      yield put(actions.markMessagesReadFailure('An unknown error occured.'));
    }
  }
}

function* handleExceededRecordedInterview(action: ReturnType<typeof actions.exceededStudentRecordedInterview>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired');
      return;
    }

    const { uni, interviewHash, interviewId, studentNumber, intakeId, offerId, pathway } = action.payload;

    const { data } = yield call(
      api.exceededStudentRecordedInterview,
      idToken,
      uni,
      interviewHash,
      studentNumber,
      intakeId,
      offerId,
      interviewId,
      pathway
    );
    yield put(actions.exceededStudentRecordedInterviewSuccess(data.interviews));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.exceededStudentRecordedInterviewFailure(err.message));
    } else {
      yield put(actions.exceededStudentRecordedInterviewFailure('An unknown error occurred.'));
    }
  }
}

function* handleGetStudentRecordedInterviews() {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: StudentState = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired');
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;

    const res: any = yield call(
      api.getStudentRecordedInterviews,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      (retryNumber: number, time: number) => {
        retryAPICallChannel.put(DataActions.retryAPICall(API_CALLS.GET_STUDENT_RECORDED_INTERVIEWS, retryNumber, time));
      },
      () => {
        retryAPICallChannel.put(DataActions.retryAPICallSuccess(API_CALLS.GET_STUDENT_RECORDED_INTERVIEWS));
      },
      (e: Error) => {
        retryAPICallChannel.put(DataActions.retryAPICallFailure(API_CALLS.GET_STUDENT_RECORDED_INTERVIEWS, e.message));
      }
    );
    if (res) {
      yield put(actions.getStudentRecordedInterviewsSuccess(res.interviews));
    }
  } catch (err: any) {
    yield put(actions.getStudentRecordFailure(err.message));
  }
}

function* watchSaveFinanceDocument() {
  yield takeEvery(StudentActionTypes.SAVE_FINANCE_DOCUMENTS, handleSaveFinanceDocuments);
}

function* watchSaveDocuments() {
  yield takeEvery(StudentActionTypes.SAVE_DOCUMENTS, handleSaveDocuments);
}
function* watchSaveFollowUpDocuments() {
  yield takeEvery(StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS, handleSaveFollowUpDocuments);
}

function* watchSaveFollowUpDocumentsBRPWillNotReceiving() {
  yield takeEvery(
    StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS_BRP_WILL_NOT_RECEIVING,
    handleSaveFollowUpDocumentsBRPWillNotReceiving
  );
}

function* watchSaveFollowUpDocumentsShareCode() {
  yield takeEvery(StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS_SHARE_CODE, handleSaveFollowUpDocumentShareCode);
}

function* watchGetStudentRecord() {
  yield takeEvery(StudentActionTypes.GET_STUDENT_RECORD, handleGetOfferRecord);
}
function* watchGetStudentDocs() {
  yield takeEvery(StudentActionTypes.GET_STUDENT_DOCS, handleGetStudentDocs);
  yield takeEvery(StudentActionTypes.SAVE_DOCUMENTS_SUCCESS, handleGetStudentDocs);
}
function* watchGetStudentFinDocs() {
  yield takeEvery(StudentActionTypes.GET_STUDENT_FIN_DOCS, handleGetStudentFinDocs);
  yield takeEvery(StudentActionTypes.SAVE_FINANCE_DOCUMENTS_SUCCESS, handleGetStudentFinDocs);
}
function* watchGetStudentFollowUpDocs() {
  yield takeEvery(StudentActionTypes.GET_STUDENT_FOLLOW_UP_DOCS, handleGetStudentFollowUpDocs);
  yield takeEvery(StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS_SUCCESS, handleGetStudentFollowUpDocs);
  yield takeEvery(StudentActionTypes.SAVE_VIEWED_BYPASS_WELCOME_MESSAGE_SUCCESS, handleGetStudentFollowUpDocs);
}
function* watchGetStudentMessages() {
  yield takeEvery(StudentActionTypes.GET_STUDENT_MESSAGES, handleGetStudentMessages);
}

function* watchSavePersonalInfo() {
  yield takeEvery(StudentActionTypes.SAVE_PERSONAL_INFO, handleSavePersonalInfo);
}

function* watchSaveHistory() {
  yield takeEvery(StudentActionTypes.SAVE_HISTORY, handleSaveHistory);
}

function* watchSaveConsents() {
  yield takeEvery(StudentActionTypes.SAVE_CONSENTS, handleSaveConsents);
}

function* watchSavePreCasInterview() {
  yield takeEvery(StudentActionTypes.SAVE_PRE_CAS_INTERVIEW, handleSavePreCasInterview);
}

function* watchSaveVisaPrepForm() {
  yield takeEvery(StudentActionTypes.SAVE_VISA_PREP_FORM, handleSaveVisaPrepForm);
}

function* watchUpdateMessage() {
  yield takeEvery(StudentActionTypes.SAVE_MESSAGE, handleSaveMessage);
}

function* watchUnsubscribe() {
  yield takeEvery(StudentActionTypes.UNSUBSCRIBE, handleUnsubscribe);
}

function* watchWSMessage() {
  yield takeEvery(StudentActionTypes.STUDENT_WEBSOCKET_CHANGE, handleWebsocketMessage);
}

function* watchGetStudentRecordedInterviews() {
  yield takeEvery(StudentActionTypes.GET_STUDENT_RECORDED_INTERVIEWS, handleGetStudentRecordedInterviews);
}

function* watchSaveFinance() {
  yield takeEvery(StudentActionTypes.SAVE_DEPOSIT, handleSaveDeposit);
  yield takeEvery(StudentActionTypes.SAVE_VIEWED_BYPASS_WELCOME_MESSAGE, handleSaveHasViewedBypassWelcomeMessage);

  yield takeEvery(StudentActionTypes.CREATE_ORGANISATION_SPONSOR, handleCreateOrganisationSponsor);
  yield takeEvery(StudentActionTypes.UPDATE_ORGANISATION_SPONSOR, handleUpdateOrganisationSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_ORGANISATION_SPONSOR, handleRemoveOrganisationSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_ORGANISATION_REGION, handleRemoveOrganisationRegion);

  yield takeEvery(StudentActionTypes.CREATE_EDUCATION_SPONSOR, handleCreateEducationSponsor);
  yield takeEvery(StudentActionTypes.UPDATE_EDUCATION_SPONSOR, handleUpdateEducationSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_EDUCATION_SPONSOR, handleRemoveEducationSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_EDUCATION_REGION, handleRemoveEducationRegion);

  yield takeEvery(StudentActionTypes.CREATE_SCHOLARSHIP_SPONSOR, handleCreateScholarshipSponsor);
  yield takeEvery(StudentActionTypes.UPDATE_SCHOLARSHIP_SPONSOR, handleUpdateScholarshipSponsor);
  yield takeEvery(StudentActionTypes.BULK_UPDATE_SCHOLARSHIP_SPONSOR, handleBulkUpdateScholarshipSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_SCHOLARSHIP_SPONSOR, handleRemoveScholarshipSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_SCHOLARSHIP_REGION, handleRemoveScholarshipRegion);

  yield takeEvery(StudentActionTypes.CREATE_FAMILY_SPONSOR, handleCreateFamilySponsor);
  yield takeEvery(StudentActionTypes.UPDATE_FAMILY_SPONSOR, handleUpdateFamilySponsor);
  yield takeEvery(StudentActionTypes.REMOVE_FAMILY_SPONSOR, handleRemoveFamilySponsor);
  yield takeEvery(StudentActionTypes.REMOVE_FAMILY_REGION, handleRemoveFamilyRegion);

  yield takeEvery(StudentActionTypes.CREATE_OTHER_SPONSOR, handleCreateOtherSponsor);
  yield takeEvery(StudentActionTypes.UPDATE_OTHER_SPONSOR, handleUpdateOtherSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_OTHER_SPONSOR, handleRemoveOtherSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_OTHER_REGION, handleRemoveOtherRegion);
}

function* watchUpdateDocMeta() {
  yield takeEvery(DocActionTypes.UPDATE_ATAS_PENDING, handleUpdateATASApplied);
  yield takeEvery(DocActionTypes.UPDATE_TB_TEST_META, handleUpdateTBTestNoDoc);
  yield takeEvery(DocActionTypes.UPDATE_VISA_REFUSAL_META_DATA, handleUpdateVisaRefusalMetaData);
  yield takeEvery(FinDocActionTypes.SAVE_METADATA_FOR_NO_DOC, handleSaveFinanceMetaDataForNoDoc);
  yield takeEvery(DocActionTypes.ADD_REQUIRED_DOCUMENT, handleAddRequiredDoc);
  yield takeEvery(FinDocActionTypes.ADD_REQUIRED_DOCUMENT, handleAddRequiredFinanceDoc);
  yield takeEvery(DocActionTypes.NO_BIOMETRICS, handleNoBiometricsDoc);
  yield takeEvery(DocActionTypes.TOGGLE_ACKNOWLEDGE_DOC, handleToggleAcknowledge);
  yield takeEvery(FinDocActionTypes.TOGGLE_ACKNOWLEDGE_DOC, handleToggleFinAcknowledge);
}

function* watchMarkMessagesRead() {
  yield takeEvery(StudentActionTypes.MARK_MESSAGES_READ, handleMarkMessagesRead);
}

function* watchSaveTravelStatus() {
  yield takeEvery(StudentActionTypes.SAVE_TRAVEL_STATUS, handleSaveTravelStatus);
}
function* watchSaveVisaStatus() {
  yield takeEvery(StudentActionTypes.SAVE_VISA_STATUS, handleSaveVisaStatus);
}
function* watchSaveRecordedInterviewAttempt() {
  yield takeEvery(StudentActionTypes.SAVE_RECORDED_INTERVIEW_ATTEMPT, handleSaveRecordedInterviewAttempt);
}
function* watchSaveAccommodationStatus() {
  yield takeEvery(StudentActionTypes.SAVE_ACCOMMODATION_STATUS, handleSaveAccommodationStatus);
}
function* watchExceededRecordedInterview() {
  yield takeEvery(StudentActionTypes.EXCEED_RECORDED_INTERVIEW, handleExceededRecordedInterview);
}
function* StudentSaga(): Generator<AllEffect<ForkEffect<void>>, void, unknown> {
  yield all([fork(watchGetStudentRecord)]);
  yield all([fork(watchGetStudentDocs)]);
  yield all([fork(watchGetStudentFinDocs)]);
  yield all([fork(watchGetStudentFollowUpDocs)]);
  yield all([fork(watchGetStudentMessages)]);
  yield all([fork(watchSavePersonalInfo)]);
  yield all([fork(watchSaveHistory)]);
  yield all([fork(watchSaveConsents)]);
  yield all([fork(watchSaveTravelStatus)]);
  yield all([fork(watchSaveVisaStatus)]);
  yield all([fork(watchSaveRecordedInterviewAttempt)]);
  yield all([fork(watchSaveAccommodationStatus)]);
  yield all([fork(watchUpdateDocMeta)]);
  yield all([fork(watchSaveDocuments)]);
  yield all([fork(watchSaveFollowUpDocuments)]);
  yield all([fork(watchSaveFollowUpDocumentsBRPWillNotReceiving)]);
  yield all([fork(watchSaveFollowUpDocumentsShareCode)]);
  yield all([fork(watchSaveFinanceDocument)]);
  yield all([fork(watchSavePreCasInterview)]);
  yield all([fork(watchSaveVisaPrepForm)]);
  yield all([fork(watchUpdateMessage)]);
  yield all([fork(watchWSMessage)]);
  yield all([fork(watchUnsubscribe)]);
  yield all([fork(watchSaveFinance)]);
  yield all([fork(watchMarkMessagesRead)]);
  yield all([fork(watchExceededRecordedInterview)]);
  yield all([fork(watchGetStudentRecordedInterviews)]);
}

export default StudentSaga;
